import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { HardHat, LogOut, Play, Square, Loader2, MapPin, Clock } from "lucide-react";

export default function EmployeePage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [obras, setObras] = useState([]);
  const [entries, setEntries] = useState([]);
  const [selectedObra, setSelectedObra] = useState("");
  const [tipo, setTipo] = useState(user?.tipo_trabalho || "");
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [elapsed, setElapsed] = useState("00:00:00");
  const timer = useRef(null);

  const load = () =>
    Promise.all([api.get("/time/status"), api.get("/obras"), api.get("/time/my")])
      .then(([s, o, m]) => {
        setStatus(s.data);
        setObras(o.data);
        setEntries(m.data);
      })
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    clearInterval(timer.current);
    if (status?.active && status.entry?.clock_in) {
      const start = new Date(status.entry.clock_in).getTime();
      const tick = () => {
        const diff = Math.max(0, Date.now() - start);
        const h = String(Math.floor(diff / 3600000)).padStart(2, "0");
        const m = String(Math.floor((diff % 3600000) / 60000)).padStart(2, "0");
        const s = String(Math.floor((diff % 60000) / 1000)).padStart(2, "0");
        setElapsed(`${h}:${m}:${s}`);
      };
      tick();
      timer.current = setInterval(tick, 1000);
    }
    return () => clearInterval(timer.current);
  }, [status]);

  const doClockIn = async () => {
    setActing(true);
    try {
      await api.post("/time/clock-in", { obra_id: selectedObra || null, tipo_trabalho: tipo });
      toast.success("Entrada registada");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setActing(false);
    }
  };

  const doClockOut = async () => {
    setActing(true);
    try {
      await api.post("/time/clock-out", {});
      toast.success("Saída registada");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setActing(false);
    }
  };

  const doLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const active = status?.active;

  return (
    <div className="min-h-screen bg-background">
      <header className="flex items-center justify-between h-16 px-4 border-b border-border bg-[#0E1732]">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
            <HardHat className="w-5 h-5 text-primary-foreground" />
          </div>
          <span className="font-display font-bold">Monumental Time</span>
        </div>
        <button data-testid="logout-button" onClick={doLogout} className="p-2 rounded-lg text-red-400 hover:bg-red-500/10 transition-colors">
          <LogOut className="w-5 h-5" />
        </button>
      </header>

      <main className="max-w-md mx-auto p-5" data-testid="employee-page">
        <div className="mb-6 mt-2">
          <p className="text-muted-foreground text-sm">Olá,</p>
          <h1 className="font-display text-2xl font-extrabold">{user?.name}</h1>
          {user?.funcao && <p className="text-primary text-sm mt-0.5">{user.funcao}</p>}
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-7 h-7 text-primary animate-spin" />
          </div>
        ) : (
          <>
            <div
              className={`rounded-2xl border p-6 text-center mb-5 ${
                active ? "border-emerald-500/50 bg-emerald-500/10" : "border-border bg-card"
              }`}
            >
              <p className="text-sm text-muted-foreground mb-2 flex items-center justify-center gap-1.5">
                <Clock className="w-4 h-4" /> {active ? "A trabalhar desde" : "Estado atual"}
              </p>
              {active ? (
                <>
                  <p data-testid="elapsed-timer" className="font-data text-5xl font-bold text-emerald-400 tracking-tight">
                    {elapsed}
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">
                    {status.entry.obra_name || "Sem obra"} · início{" "}
                    {new Date(status.entry.clock_in).toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" })}
                  </p>
                </>
              ) : (
                <p className="font-data text-4xl font-bold text-muted-foreground">Fora de serviço</p>
              )}
            </div>

            {!active && (
              <div className="space-y-3 mb-5">
                <div>
                  <label className="block text-sm font-medium mb-1.5 flex items-center gap-1.5">
                    <MapPin className="w-4 h-4 text-primary" /> Obra
                  </label>
                  <select
                    data-testid="obra-select"
                    value={selectedObra}
                    onChange={(e) => setSelectedObra(e.target.value)}
                    className="w-full px-3 py-3 rounded-lg bg-input border border-border focus:border-primary outline-none"
                  >
                    <option value="">— Selecionar obra —</option>
                    {obras.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5">Tipo de trabalho</label>
                  <input
                    data-testid="tipo-input"
                    value={tipo}
                    onChange={(e) => setTipo(e.target.value)}
                    placeholder="Ex: Alvenaria"
                    className="w-full px-3 py-3 rounded-lg bg-input border border-border focus:border-primary outline-none"
                  />
                </div>
              </div>
            )}

            <button
              data-testid={active ? "clock-out-button" : "clock-in-button"}
              onClick={active ? doClockOut : doClockIn}
              disabled={acting}
              className={`w-full py-6 rounded-2xl font-display text-xl font-extrabold flex items-center justify-center gap-3 transition-colors disabled:opacity-60 ${
                active
                  ? "bg-red-600 hover:bg-red-500 text-white"
                  : "bg-primary hover:bg-[#F5B05E] text-primary-foreground"
              }`}
            >
              {acting ? (
                <Loader2 className="w-6 h-6 animate-spin" />
              ) : active ? (
                <>
                  <Square className="w-6 h-6" /> Registar Saída
                </>
              ) : (
                <>
                  <Play className="w-6 h-6" /> Registar Entrada
                </>
              )}
            </button>

            <div className="mt-8">
              <h2 className="font-display font-semibold mb-3">Últimos registos</h2>
              {entries.length === 0 ? (
                <p className="text-muted-foreground text-sm text-center py-6">Sem registos ainda.</p>
              ) : (
                <div className="space-y-2">
                  {entries.slice(0, 15).map((e) => (
                    <div key={e.id} data-testid={`my-entry-${e.id}`} className="bg-card border border-border rounded-xl p-4 flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">{e.obra_name || "Sem obra"}</p>
                        <p className="text-xs text-muted-foreground font-data">
                          {fmt(e.clock_in)} → {e.clock_out ? fmt(e.clock_out) : "em curso"}
                        </p>
                      </div>
                      <span className="font-data font-bold text-primary">{e.hours ? `${e.hours}h` : "—"}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function fmt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("pt-PT", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}
